import React, { ReactElement, useState, useEffect } from 'react';
import { useFloating, useClick, useInteractions, useDismiss, autoUpdate } from '@floating-ui/react';
import { useKey } from 'react-use';
import { colors } from 'components/styles/variables';
import { styles } from './styles';
import HeaderIconButton, { IconType } from '../HeaderIconButton';

interface TopBarDropdownContainerProps {
  children: ReactElement;
  title?: string;
  placeHolderCtaText?: string;
  handleClick?: () => Promise<void>;
  filter: string;
  isIcon?: boolean;
}

export default function TopBarDropdownContainer({
  children,
  title,
  placeHolderCtaText,
  handleClick,
  filter,
  isIcon = false,
}: TopBarDropdownContainerProps): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [ctaText, setCtaText] = useState<string | undefined>(placeHolderCtaText);

  useEffect(() => {
    setCtaText(placeHolderCtaText);
  }, [placeHolderCtaText]);

  const { refs, floatingStyles, context } = useFloating({
    open: isModalOpen,
    onOpenChange: setIsModalOpen,
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  function onFilterClick(): void {
    handleClick?.();
    setIsModalOpen(!isModalOpen);
  }

  function onListItemClick(): void {
    setIsModalOpen(false);
  }

  useKey('Escape', (): void => {
    setIsModalOpen(false);
  });

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter') {
      handleClick?.();
      setIsModalOpen(true);
    }
  }

  return (
    <>
      {!isIcon && (
        <button
          ref={refs.setReference}
          className={`${filter}-filter-cta-container`}
          onClick={(): void => onFilterClick()}
        >
          <div className={`${filter}-filter-cta-header`}>{title}</div>
          <div className={`${filter}-filter-cta`}>{ctaText}</div>
        </button>
      )}

      {isIcon && (
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          onClick={(): void => onFilterClick()}
          data-testid="header-icon-button-container"
          role="button"
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          <HeaderIconButton icon={filter as IconType} />
        </div>
      )}

      {isModalOpen && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            width: filter === 'location' ? '298px' : 'auto',
            zIndex: 999,
            background: 'white',
            borderRadius: '8px',
            border: `1px solid ${colors.G2}`,
            filter: 'drop-shadow(0px 24px 38px rgba(0, 0, 0, 0.2))',
          }}
          {...getFloatingProps()}
        >
          {React.cloneElement(children, { onListItemClick })}
        </div>
      )}

      <style jsx>{styles}</style>
    </>
  );
}
